import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import {AddOutlined, DeleteOutlined, DeleteSweepOutlined, EditOutlined, SaveOutlined} from '@material-ui/icons';
import BlockUI from '../../../shared/blockui/BlockUI.tsx';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import OutlinedButton from '../../../shared/buttons/OutlinedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {criarColunaOpcoes} from '../../components/ColunaOpcoes.jsx';
import DialogPendenciaDocumento from './DialogPendenciaDocumento.jsx';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import {PAGINACAO_PADRAO, textoSemRegistros} from '../../components/paginacao.js';
import useFetch from '../../../../hooks/useFetch.js';
import useUpdate from '../../../../hooks/useUpdate.js';
import {URL_AVALIACAO_DOCUMENTOS_PENDENCIAS} from '../../../../constants/index.js';
import {buttonTableCell} from '../../../../utils/styles.js';

const PARAMS = {};
const ID_TITULO = 'dialog-pendencias-documentos-titulo';
const COMPARATOR_KEYS = ['chave'];
const FILTER_OPTIONS = {filterByColumn: true};
// A grade já está dentro do diálogo: evita Paper aninhado.
const TABLE_CONTAINER_PROPS = {component: 'div'};
const PROPRIO_INSCRITO = 'Próprio inscrito';

const useStyles = makeStyles(theme => ({
    ...buttonTableCell(theme),
    paper: {
        width: 900,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    },
    botoesGrade: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1)
    }
}));

/* Corpo do PUT: a lista inteira substitui as pendências da inscrição. */
const montarPayload = linhas => linhas.map(linha => ({
    id: linha.id,
    idDocumento: linha.idDocumento,
    descricao: linha.descricao,
    idComposicaoFamiliar: linha.idComposicaoFamiliar
}));

const nomePara = (idComposicaoFamiliar, membros) => {
    const membro = idComposicaoFamiliar === null || idComposicaoFamiliar === undefined
        ? null
        : membros.find(item => item.id === idComposicaoFamiliar);

    return membro ? membro.nome : PROPRIO_INSCRITO;
};

/*
 * Documentos pendentes da inscrição (o "Visualizar documentos pendentes" do
 * PrimeFaces), aberto pela Avaliação de Documentos sem sair da tela. A lista
 * fica em memória e só vai para o back no "Salvar" (PUT que substitui todas as
 * pendências). Adicionar e editar abrem o DialogPendenciaDocumento por cima.
 */
export default function DialogPendenciasDocumentos(props) {
    const {open, onClose, idInscricao, inscricao, tipos = [], onSalvo} = props;
    const classes = useStyles();
    const [buscar, carregando] = useFetch({returnInnerLoadingState: true});
    const [update, salvando] = useUpdate({returnInnerLoadingState: true});
    const {createToast} = useToast();
    const confirmation = useConfirmationDialog();
    const sequencia = useRef(0);
    const [membros, setMembros] = useState([]);
    const [linhas, setLinhas] = useState([]);
    const [inicial, setInicial] = useState('[]');
    const [emEdicao, setEmEdicao] = useState(null);
    const [formularioAberto, setFormularioAberto] = useState(false);

    // A cada abertura busca de novo: descarta o que ficou da vez anterior.
    useEffect(() => {
        if (!open || !idInscricao) {
            return;
        }

        setLinhas([]);
        setInicial('[]');
        buscar(URL_AVALIACAO_DOCUMENTOS_PENDENCIAS(idInscricao), PARAMS, resultado => {
            const listaMembros = resultado.membros || [];
            const carregadas = (resultado.pendencias || []).map(pendencia => ({
                chave: `pendencia-${pendencia.id}`,
                id: pendencia.id,
                // Pendência antiga pode vir sem tipo de documento (documento nulo).
                idDocumento: pendencia.documento ? pendencia.documento.id : null,
                documentoNome: pendencia.documento ? pendencia.documento.nome : '',
                descricao: pendencia.descricao,
                idComposicaoFamiliar: pendencia.idComposicaoFamiliar === undefined ? null : pendencia.idComposicaoFamiliar,
                nomePara: pendencia.nomePara || nomePara(pendencia.idComposicaoFamiliar, listaMembros)
            }));

            setMembros(listaMembros);
            setLinhas(carregadas);
            setInicial(JSON.stringify(montarPayload(carregadas)));
        });
    }, [open, idInscricao]);

    const alterado = JSON.stringify(montarPayload(linhas)) !== inicial;
    const titulo = inscricao && inscricao.nome
        ? `Documentos pendentes - ${idInscricao} / ${inscricao.nome}`
        : `Documentos pendentes - ${idInscricao}`;

    const proximaChave = () => {
        sequencia.current += 1;

        return `nova-${sequencia.current}`;
    };

    const abrirFormulario = linha => () => {
        setEmEdicao(linha);
        setFormularioAberto(true);
    };

    const salvarPendencia = dados => {
        const tipo = tipos.find(item => item.id === dados.idDocumento);
        const preenchida = {
            ...dados,
            documentoNome: tipo ? tipo.nome : '',
            nomePara: nomePara(dados.idComposicaoFamiliar, membros)
        };

        setLinhas(anteriores => emEdicao
            ? anteriores.map(linha => (linha.chave === emEdicao.chave ? {...linha, ...preenchida} : linha))
            : [...anteriores, {chave: proximaChave(), id: null, ...preenchida}]);
    };

    const remover = linha => () => confirmation(
        'Tem certeza que deseja remover esta pendência?',
        () => setLinhas(anteriores => anteriores.filter(item => item.chave !== linha.chave)));

    const removerTodas = () => confirmation(
        'Tem certeza que deseja remover todas as pendências?',
        () => setLinhas([]));

    // Fechar (botão, Esc ou clique fora) com alteração pede confirmação antes de descartar.
    const fechar = () => {
        if (!alterado) {
            onClose();
            return;
        }

        confirmation('Há alterações não salvas. Deseja descartá-las?', onClose);
    };

    const salvar = () => update(
        URL_AVALIACAO_DOCUMENTOS_PENDENCIAS(idInscricao),
        montarPayload(linhas),
        () => {
            createToast({severity: 'success', message: 'Pendências salvas com sucesso!'});
            onClose();
            onSalvo && onSalvo();
        });

    return (
        <>
            <Dialog
                open={open}
                onClose={fechar}
                maxWidth={false}
                scroll='body'
                aria-labelledby={ID_TITULO}
                classes={{paper: classes.paper}}>
                <DialogTitle id={ID_TITULO}>{titulo}</DialogTitle>
                <DialogContent classes={{root: classes.contentRoot}}>
                    <BlockUI loading={carregando}>
                        <DataGrid
                            rows={linhas}
                            comparatorKeys={COMPARATOR_KEYS}
                            filter
                            filterOptions={FILTER_OPTIONS}
                            paginate
                            paginateOptions={PAGINACAO_PADRAO}
                            tableContainerProps={TABLE_CONTAINER_PROPS}
                            noRecordsText={textoSemRegistros(carregando, 'Nenhuma pendência encontrada.')}
                            toolbarContent={
                                <div className={classes.botoesGrade}>
                                    <ContainedButton startIcon={<AddOutlined/>} onClick={abrirFormulario(null)}>
                                        Adicionar
                                    </ContainedButton>
                                    <OutlinedButton
                                        startIcon={<DeleteSweepOutlined/>}
                                        onClick={removerTodas}
                                        disabled={!linhas.length}>
                                        Remover todas
                                    </OutlinedButton>
                                </div>
                            }
                            columns={[
                                {label: 'Tipo', field: 'documentoNome', filterable: true, valueGetter: valor => ouTraco(valor)},
                                {label: 'Descrição', field: 'descricao', filterable: true, valueGetter: valor => ouTraco(valor)},
                                {label: 'Documento para', field: 'nomePara', filterable: true, valueGetter: valor => ouTraco(valor)},
                                criarColunaOpcoes(linha => [
                                    {titulo: 'Editar', icone: <EditOutlined/>, onClick: abrirFormulario(linha)},
                                    {titulo: 'Remover', icone: <DeleteOutlined/>, onClick: remover(linha)}
                                ], {className: classes.buttonTableCell})
                            ]}/>
                    </BlockUI>
                </DialogContent>
                <DialogActions>
                    <TextButton onClick={fechar}>Fechar</TextButton>
                    <ContainedButton startIcon={<SaveOutlined/>} onClick={salvar} disabled={!alterado || salvando}>
                        Salvar
                    </ContainedButton>
                </DialogActions>
            </Dialog>
            <DialogPendenciaDocumento
                open={formularioAberto}
                onClose={() => setFormularioAberto(false)}
                pendencia={emEdicao}
                tipos={tipos}
                membros={membros}
                onSalvar={salvarPendencia}/>
        </>
    );
}

DialogPendenciasDocumentos.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /* Id da inscrição (vem da rota da Avaliação de Documentos). */
    idInscricao: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    /* Inscrição resumida da avaliação ({id, nome}), só para o título. */
    inscricao: PropTypes.object,
    /* Documentos do processo seletivo: [{id, nome}]. */
    tipos: PropTypes.array,
    /* Chamado depois de salvar (a tela recarrega a avaliação). */
    onSalvo: PropTypes.func
};
